import React, { ChangeEvent } from "react";
import * as _ from "lodash";

import DatePicker from "react-datepicker";

import type { FormDrawerState, TField, FormDrawerProps } from "./CreateUserDrawer";

import { Utils } from "@/common/utils";

import { Col, FormGroup, FormLabel, FormControl, Form, FormCheck, Stack, Alert } from "react-bootstrap";

import { SelectWrapper } from "Components/Common/SelectWrapper";
import { Committee, Student } from "Stores/commonStore";
import { CourseBasic } from "@/hooks/courses";

type TUserForm = {
    state: FormDrawerState;
    setField: <K extends TField>(field: TField, value: FormDrawerState[K]) => void;
    courses: CourseBasic[];
    students: Student[];
    committees: Committee[];
} & Pick<FormDrawerProps, "userStore" | "domainWhitelist">;

const reactSelectModalStyle = {
    control: (baseStyles) => ({
        ...baseStyles,
        minHeight: "48px",
        borderRadius: "6px",
    }),
    valueContainer: (baseStyles) => ({
        ...baseStyles,
        padding: "0 12px",
        maxHeight: 120,
        overflowY: "auto",
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 2147483660,
    }),
};

const CreateUserForm = ({ state, setField, courses, students, committees, userStore, domainWhitelist }: TUserForm) => {
    const getId = (obj: any) => obj.id;
    const getStudentId = (obj: any) => obj.ruuid;
    const getLabel = (obj: any) => obj.name;

    const isEmailDomainValid = () => {
        const parts = state.email.split("@");
        const domain = parts[1];
        if (!domain) return true;

        return !domainWhitelist || domainWhitelist.includes(domain);
    };

    const updateGroups = (groups: string[]) => {
        setField("groups", [..._.uniq([...groups])]);
    };

    const handleIsAdminChange = (isAdmin: boolean) => {
        let updatedGroups = state.groups.slice();

        if (isAdmin) {
            updatedGroups.push("administrators");
        } else {
            updatedGroups = updatedGroups.filter((group) => group !== "administrators");

            setField("isKeyAdmin", false);
        }

        // batch update group
        updateGroups(updatedGroups);
        setField("isAdmin", isAdmin);
    };

    const handleIsKeyAdminChange = (isKeyAdmin: boolean) => {
        let updatedGroups = [...state.groups];

        if (isKeyAdmin && userStore) {
            userStore?.fetchKeyAdminUser().then((user) => {
                if (user) {
                    setField("isKeyAdminNote", `There is already a Key Admin assigned for this school. Saving this user will replace ${user.name} (${user.email}) as Key Admin.`);
                }
            });
        }

        if (isKeyAdmin) {
            if (!updatedGroups.includes("administrators")) {
                updatedGroups.push("administrators");
            }
            updatedGroups.push("key_administrators");
            setField("isAdmin", true);
        } else {
            updatedGroups = updatedGroups.filter((group) => group !== "key_administrators");
            setField("isKeyAdminNote", null);
        }

        updateGroups(updatedGroups);
        setField("isKeyAdmin", isKeyAdmin);
    };

    return (
        <div className="form-section-group">
            {/* General */}
            <div className="form-section">
                <div className="title-form-section">General</div>

                <Stack direction="horizontal" gap={2}>
                    <FormGroup as={Col} sm="4" controlId="user-form-prefix">
                        <FormLabel>Prefix (Optional)</FormLabel>
                        <FormControl type="input" placeholder="E.g. Dra." value={state.prefix} onChange={(e: ChangeEvent<HTMLInputElement>) => setField("prefix", e.target.value)} />
                    </FormGroup>
                    <FormGroup as={Col} controlId="user-form-name-first">
                        <FormLabel>First name</FormLabel>
                        <FormControl required type="input" placeholder="Enter the first name" value={state.firstName} onChange={(e: ChangeEvent<HTMLInputElement>) => setField("firstName", e.target.value)} />
                        <span />
                    </FormGroup>
                </Stack>

                <Stack direction="horizontal" gap={2}>
                    <FormGroup as={Col} controlId="user-form-name-last">
                        <FormLabel>Last name</FormLabel>
                        <FormControl required type="input" placeholder="Enter the last name" value={state.lastName} onChange={(e: ChangeEvent<HTMLInputElement>) => setField("lastName", e.target.value)} />
                        <span />
                    </FormGroup>
                    <FormGroup as={Col} sm="4" controlId="user-form-suffix">
                        <FormLabel>Suffix (Optional)</FormLabel>
                        <FormControl type="input" placeholder="E.g. Jr." value={state.suffix} onChange={(e: ChangeEvent<HTMLInputElement>) => setField("suffix", e.target.value)} />
                    </FormGroup>
                </Stack>

                <FormGroup controlId="user-form-email">
                    <FormLabel>Email</FormLabel>
                    <FormControl required type="email" placeholder="Enter the email" value={state.email} onChange={(e: ChangeEvent<HTMLInputElement>) => setField("email", e.target.value)} isInvalid={!isEmailDomainValid()} />
                    <span />
                    <Form.Control.Feedback type="invalid">Warning: Email is not in the whitelist</Form.Control.Feedback>
                </FormGroup>
            </div>

            {/* Config */}
            <div className="form-section">
                <div className="title-form-section mb-2">Configuration</div>

                <Stack direction="horizontal" gap={2} className="mb-2">
                    <Stack>
                        <FormLabel htmlFor="user-form-status">User Status</FormLabel>
                        <div className="status-indicator">{state.isActive ? "Active" : "Inactive"}</div>
                    </Stack>

                    <FormCheck type="switch" className="ms-auto form-check__toggle" id="user-form-status" onChange={(e: ChangeEvent<HTMLInputElement>) => setField("isActive", e.target.checked)} checked={state.isActive} />
                </Stack>

                <Stack id="user-role" direction="vertical" gap={3} className="mb-2" aria-label="switch buttons to select admin or key admin role for a user">
                    <Stack direction="horizontal" gap={2} aria-label="switcher/toggler to select user role as administrator">
                        <FormLabel htmlFor="user-role-admin">Administrator</FormLabel>

                        <FormCheck type="switch" className="ms-auto form-check__toggle" id="user-role-admin" onChange={(e: ChangeEvent<HTMLInputElement>) => handleIsAdminChange(e.target.checked)} checked={state.isAdmin} />
                    </Stack>

                    <Stack direction="horizontal" gap={2} aria-label="switcher/toggler to select user role as key admin">
                        <FormLabel htmlFor="user-role-key-admin">Key Admin</FormLabel>

                        <FormCheck type="switch" disabled={!state.isAdmin} className="ms-auto form-check__toggle" id="user-role-key-admin" onChange={(e: ChangeEvent<HTMLInputElement>) => handleIsKeyAdminChange(e.target.checked)} checked={state.isKeyAdmin} />
                    </Stack>

                    {state.isKeyAdminNote ? (
                        <Alert key="warning" variant="warning" className="mt-3">
                            {state.isKeyAdminNote}
                        </Alert>
                    ) : null}
                </Stack>

                <FormGroup controlId="user-expiry-date">
                    <FormLabel>User Expiry (Optional)</FormLabel>
                    <DatePicker id="user-expiry-date" placeholderText="MM/DD/YYYY" selected={state.removeOn} onChange={(date: Date | null) => setField("removeOn", date)} />
                </FormGroup>
            </div>

            {/* Additional */}
            <div className="form-section">
                <div className="title-form-section">Additional Info</div>

                <Stack gap={2} className="additional-info">
                    <FormGroup controlId="user-form-students">
                        <FormLabel>Mentees</FormLabel>
                        <SelectWrapper id="user-form-students" hideSelectedOptions={true} onChange={(next: any) => setField("students", next)} isClearable={true} isMulti={true} options={students || []} placeholder="Selectan option" value={state.students} getOptionLabel={Utils.studentToStudentLabel} getOptionValue={getStudentId} styles={reactSelectModalStyle} menuPortalTarget={document.body} menuPosition="fixed" className="react-select-container" />
                    </FormGroup>

                    <FormGroup controlId="user-form-courses">
                        <FormLabel>Courses</FormLabel>
                        <SelectWrapper id="user-form-courses" hideSelectedOptions={true} onChange={(next: any) => setField("courses", next)} isClearable={true} isMulti={true} options={courses || []} placeholder="Select an option" value={state.courses} getOptionLabel={getLabel} getOptionValue={getId} styles={reactSelectModalStyle} menuPortalTarget={document.body} menuPosition="fixed" className="react-select-container" />
                    </FormGroup>

                    <FormGroup controlId="user-form-committees">
                        <FormLabel>Committees</FormLabel>
                        <SelectWrapper id="user-form-committees" hideSelectedOptions={true} onChange={(next: any) => setField("committees", next)} isClearable={true} isMulti={true} options={committees || []} placeholder="Select an option" value={state.committees} getOptionLabel={getLabel} getOptionValue={getId} styles={reactSelectModalStyle} menuPortalTarget={document.body} menuPosition="fixed" className="react-select-container" />
                    </FormGroup>
                </Stack>
            </div>
        </div>
    );
};

export default CreateUserForm;
